import React, {
  Children,
  FC,
  PropsWithChildren,
  ReactElement,
  ReactNode,
  isValidElement,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'

import { useModalInstance, useModalTabsOptional } from '@context/ModalInstanceContext'

import { SegmentedControl } from '@ui/components/SegmentedControl/SegmentedControl'
import { Tabs } from '@ui/components/Tabs'
import type { Tab } from '@ui/components/Tabs'
import { TabSize } from '@ui/components/Tabs/TabItem/TabItem'
import type { IconFCType } from '@ui/icons/types'

import { Box } from '@shared/components/Box'

type ModalBodyTabsProps = PropsWithChildren<{
  initialIndex?: number
  size?: TabSize
  variant?: 'tabs' | 'segmented-control'
  onTabChange?(index: number, tab: Tab): void
  attachToHeader?: boolean
  fullWidth?: boolean
  center?: boolean
}>

type ModalBodyTabsTabProps = PropsWithChildren<{
  title: string
  id: string
  iconLeft?: IconFCType
  iconRight?: IconFCType
  alert?: boolean
}>

type TabDesc = {
  header: Tab
  content: React.ReactNode
  idStr: string
  label: string
}

function isTabElement(
  node: ReactNode
): node is ReactElement<ModalBodyTabsTabProps, typeof ModalBodyTabsTab> {
  return isValidElement(node) && node.type === ModalBodyTabsTab
}

const ModalBodyTabsRoot: FC<ModalBodyTabsProps> = ({
  children,
  initialIndex = 0,
  size = 'md',
  onTabChange,
  attachToHeader = false,
  fullWidth = false,
  center = false,
  variant = 'tabs',
}) => {
  const tabsCtx = useModalTabsOptional()
  const modal = useModalInstance()

  const items: TabDesc[] = useMemo(() => {
    const nodes = Children.toArray(children)
    return nodes.filter(isTabElement).map((el, index) => {
      const { title, id, iconLeft, iconRight, alert, children: panel } = el.props
      const idStr = String(`${id}-tab-${modal.id}-${index}`)
      return {
        label: title,
        idStr,
        content: panel,
        header: {
          id: idStr,
          label: title,
          iconLeft,
          iconRight,
          alert,
        },
      }
    })
  }, [children, modal.id])

  const headerItems = useMemo(
    () =>
      items.map((x) => ({
        id: x.idStr,
        label: x.label,
        iconLeft: x.header.iconLeft,
        iconRight: x.header.iconRight,
        alert: x.header.alert,
      })),
    [items]
  )

  const headerItemsKey = useMemo(
    () => headerItems.map((i) => `${i.id}|${i.label}|${i.alert ? 1 : 0}`).join(','),
    [headerItems]
  )

  const setItems = tabsCtx?.setItems
  const setSize = tabsCtx?.setSize
  const setActiveId = tabsCtx?.setActiveId
  const activeIdRef = useRef(tabsCtx?.activeId)
  useEffect(() => {
    activeIdRef.current = tabsCtx?.activeId
  }, [tabsCtx?.activeId])

  useEffect(() => {
    if (!attachToHeader || !tabsCtx || !setItems || !setSize) return
    setSize(size)
    setItems(headerItems)
    tabsCtx.setVariant?.(variant)
    if (!activeIdRef.current && items[initialIndex]) {
      setActiveId?.(items[initialIndex].idStr)
    }
  }, [
    attachToHeader,
    size,
    initialIndex,
    headerItemsKey,
    setItems,
    setSize,
    setActiveId,
    tabsCtx,
    items,
    variant,
  ])

  const clearItemsRef = useRef<typeof setItems>(null)
  useEffect(() => {
    clearItemsRef.current = setItems
  }, [setItems])
  useEffect(() => {
    return () => {
      if (attachToHeader && clearItemsRef.current) clearItemsRef.current([])
    }
  }, [attachToHeader])

  const [localActive, setLocalActive] = useState(
    Math.min(Math.max(initialIndex, 0), Math.max(items.length - 1, 0))
  )
  const headerTabs: Tab[] = useMemo(() => items.map((t) => t.header), [items])

  const handleTabClick = (tab: Tab) => {
    const idx = headerTabs.findIndex((t) => String(t.id ?? t.path) === String(tab.id ?? tab.path))
    const next = idx >= 0 ? idx : 0
    setLocalActive(next)
    onTabChange?.(next, tab)
  }

  const activeId = attachToHeader && tabsCtx ? tabsCtx.activeId : items[localActive]?.idStr

  if (items.length === 0) return null

  const ModalTabs = () => {
    const tabVariant = tabsCtx?.variant ?? variant

    if (!(attachToHeader && tabsCtx)) {
      if (tabVariant == 'tabs') {
        return (
          <Tabs
            tabs={headerTabs}
            initialIndex={localActive}
            size={size}
            onTabClick={handleTabClick}
            center={center}
          />
        )
      }

      if (tabVariant == 'segmented-control') {
        return (
          <SegmentedControl
            initialTab={localActive}
            size={size === 'xs' ? 'sm' : 'md'}
            variant='default'
            activeTab={localActive}
            tabs={headerTabs.map((x) => ({
              id: String(x.id),
              name: x.label,
            }))}
          />
        )
      }
    }

    return null
  }

  const ModalBody = () => {
    return (
      <Box role='region' fullWidth>
        {items.map((t) => (
          <div
            id={`${t.idStr}-panel`}
            role='tabpanel'
            aria-labelledby={`${t.idStr}-tab`}
            style={{
              display: t.idStr === activeId ? 'block' : 'none',
              width: '100%',
              minHeight: 0,
            }}
          >
            {t.content}
          </div>
        ))}
      </Box>
    )
  }

  return (
    <Box flex direction='column' gap='x16' style={{ minHeight: 0 }} fullWidth={fullWidth}>
      <ModalTabs />
      <ModalBody />
    </Box>
  )
}

const ModalBodyTabsTab: FC<ModalBodyTabsTabProps> = ({ children }) => <>{children}</>

export const ModalBodyTabs = Object.assign(ModalBodyTabsRoot, {
  Tab: ModalBodyTabsTab,
})
